import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  inject,
  EventEmitter,
  HostBinding,
  Input,
  OnChanges,
  OnDestroy,
  OnInit,
  Output,
} from '@angular/core';

export type SaleSplashApp = 'pasajeros' | 'conductores';

interface SplashTheme {
  background: string;
  tagline: string;
  label: string | null;
}

const THEMES: Record<SaleSplashApp, SplashTheme> = {
  pasajeros: {
    background: '#117F84',
    tagline: 'Busca tu ruta, guárdala y mira cuándo llega tu minibús.',
    label: null,
  },
  conductores: {
    background: '#7D0033',
    tagline: 'Conéctate, sigue tu recorrido y mantente actualizado.',
    label: 'CONDUCTORES',
  },
};

/** Intro animation length (lines, logo, text). The bar keeps filling until `ready`. */
const INTRO_MS = 1600;
const FADE_OUT_MS = 360;

/**
 * Animated Sale splash screen for the Pasajeros and Conductores apps.
 *
 * Shows as a full-screen overlay on top of the app. It stays up until BOTH
 * `minDurationMs` has passed AND `ready` is true, then fades out and emits
 * `finished`. Remove it from the DOM on `finished`.
 */
@Component({
  selector: 'sale-splash',
  standalone: true,
  templateUrl: './sale-splash.component.html',
  styleUrls: ['./sale-splash.component.css'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class SaleSplashComponent implements OnInit, OnChanges, OnDestroy {
  /** Which app's splash to show. */
  @Input() app: SaleSplashApp = 'pasajeros';
  /** Set to true when the app has finished loading (auth check, config, first data...). */
  @Input() ready = false;
  /** Optional real progress 0..1. Leave null for the automatic bar. */
  @Input() progress: number | null = null;
  /** Minimum time on screen, so the intro animation always plays fully. */
  @Input() minDurationMs = 2400;
  /**
   * Hard ceiling. `ready` is driven by app startup, which can stall rather
   * than fail — a native permission dialog left unanswered, a request with no
   * timeout — and a splash that waits on it forever hides a working app
   * behind a loading bar. Past this point it leaves regardless. Set beyond the
   * slowest legitimate startup (GPS resolution ~13s) so it only ever fires on
   * a genuine stall.
   */
  @Input() maxDurationMs = 15000;
  /** Footer line. */
  @Input() footer = 'Syncc - Dos Mobility S.R.L.';

  /** Emitted once the fade-out has finished. */
  @Output() finished = new EventEmitter<void>();

  @HostBinding('class.is-leaving') leaving = false;
  @HostBinding('style.--sp-bg') get bg(): string {
    return this.theme.background;
  }
  @HostBinding('style.--sp-fade-out') readonly fadeOut = `${FADE_OUT_MS}ms`;

  private readonly cdr = inject(ChangeDetectorRef);
  private minElapsed = false;
  private timedOut = false;
  private timers: ReturnType<typeof setTimeout>[] = [];

  get theme(): SplashTheme {
    return THEMES[this.app] ?? THEMES.pasajeros;
  }

  get barWidth(): string | null {
    if (this.leaving) return '100%';
    if (this.progress == null) return null;
    const p = Math.min(1, Math.max(0, this.progress));
    return `${p * 100}%`;
  }

  ngOnInit(): void {
    const min = Math.max(this.minDurationMs, INTRO_MS);
    this.timers.push(
      setTimeout(() => {
        this.minElapsed = true;
        this.maybeLeave();
      }, min),
    );
    this.timers.push(
      setTimeout(() => {
        this.timedOut = true;
        this.maybeLeave();
      }, Math.max(this.maxDurationMs, min)),
    );
  }

  ngOnChanges(): void {
    this.maybeLeave();
  }

  ngOnDestroy(): void {
    this.timers.forEach(clearTimeout);
  }

  private maybeLeave(): void {
    if (this.leaving || !this.minElapsed) return;
    if (!this.ready && !this.timedOut) return;
    this.leaving = true;
    this.cdr.markForCheck();
    this.timers.push(setTimeout(() => this.finished.emit(), FADE_OUT_MS));
  }
}
